import React, { useState, useEffect, useRef } from "react";
import {
  FaHeart,
  FaRupeeSign,
  FaUser,
  FaEnvelope,
  FaPhone,
  FaShieldAlt,
} from "react-icons/fa";

const DonationPage = () => {
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    amount: "",
    paymentMethod: "card",
  });

  const [customAmount, setCustomAmount] = useState("");
  const [selectedAmount, setSelectedAmount] = useState("");
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  const presetAmounts = [500, 1000, 2000, 5000];

  useEffect(() => {
    const currentSection = sectionRef.current;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.1 }
    );

    if (currentSection) observer.observe(currentSection);

    return () => {
      if (currentSection) observer.unobserve(currentSection);
    };
  }, []);

  const handleAmountSelect = (amt) => {
    setSelectedAmount(amt);
    setFormData({ ...formData, amount: amt });
    setCustomAmount("");
  };

  const handleCustomAmount = (e) => {
    const val = e.target.value;
    setCustomAmount(val);
    setSelectedAmount("");
    setFormData({ ...formData, amount: val });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log("Donation Data:", formData);
    alert("Thank you for your donation!");
  };

  return (
    <div
      ref={sectionRef}
      className="relative min-h-screen overflow-hidden bg-gradient-to-br from-[#eafaff] via-white to-[#dff7fb] py-14 px-4 sm:px-6 lg:px-8"
    >
      <div className="absolute top-10 left-0 w-72 h-72 bg-[#1298b5]/10 rounded-full blur-3xl"></div>
      <div className="absolute bottom-10 right-0 w-80 h-80 bg-[#f97316]/10 rounded-full blur-3xl"></div>

      <div className="relative max-w-7xl mx-auto">
        <div
          className={`grid grid-cols-1 lg:grid-cols-2 gap-8 items-stretch transition-all duration-700 ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
          }`}
        >
          <div className="bg-white/90 backdrop-blur-sm rounded-3xl p-7 md:p-8 shadow-xl border border-[#d8f4f8] flex flex-col h-full">
            <img
              src="https://images.rawpixel.com/image_800/czNmcy1wcml2YXRlL3Jhd3BpeGVsX2ltYWdlcy93ZWJzaXRlX2NvbnRlbnQvbHIvczkzLXBtLTI3NTdfMS5qcGc.jpg"
              alt="Donation cause - supporting education and livelihood"
              className="w-full h-52 sm:h-64 object-cover rounded-2xl mb-6 shadow-md"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src =
                  "https://via.placeholder.com/800x400?text=Support+Our+Cause";
              }}
            />

            <div className="flex-1">
              <span className="inline-block bg-[#eafaff] text-[#1298b5] border border-[#bdeef5] px-4 py-1.5 rounded-full text-sm font-semibold mb-4">
                Support The Cause
              </span>

              <h1 className="text-4xl md:text-5xl font-extrabold text-[#073b4c] leading-tight mb-4">
                Donors{" "}
                <span className="text-[#1298b5]">
                  Make Real Change
                </span>
              </h1>

              <blockquote className="text-xl md:text-2xl italic text-[#073b4c] border-l-4 border-[#f97316] pl-4 mb-5">
                “Invest in a Life, Not Just a Project.”
              </blockquote>

              <p className="text-base md:text-lg text-gray-600 mb-6 leading-relaxed">
                Your contribution helps a mother send her child to school and
                ensures a green future for the next generation. Join us in
                making a tangible difference today.
              </p>

              <div className="mb-8">
                <a
                  href="#donate"
                  className="inline-flex items-center px-8 py-3.5 bg-gradient-to-r from-[#1298b5] to-[#0b819b] hover:from-[#f97316] hover:to-[#fb923c] text-white text-lg font-semibold rounded-xl shadow-lg transition-all duration-300 hover:scale-105"
                >
                  <FaHeart className="mr-2" /> Donate Now
                </a>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 mt-auto pt-6">
              <div className="bg-[#eafaff] border border-[#d8f4f8] p-4 rounded-2xl shadow-sm">
                <div className="text-3xl font-extrabold text-[#1298b5]">
                  500+
                </div>
                <div className="text-sm text-gray-600">Lives Impacted</div>
              </div>

              <div className="bg-[#fff7ed] border border-[#fed7aa] p-4 rounded-2xl shadow-sm">
                <div className="text-3xl font-extrabold text-[#f97316]">
                  10+
                </div>
                <div className="text-sm text-gray-600">Projects Running</div>
              </div>
            </div>
          </div>

          <div
            id="donate"
            className="bg-white rounded-3xl shadow-2xl p-7 md:p-8 border border-[#d8f4f8] h-full flex flex-col"
          >
            <h2 className="text-2xl font-extrabold text-[#073b4c] mb-6 flex items-center">
              <span className="w-11 h-11 bg-[#eafaff] rounded-xl flex items-center justify-center text-[#1298b5] mr-3">
                <FaRupeeSign />
              </span>
              Make a Donation
            </h2>

            <form onSubmit={handleSubmit} className="space-y-5 flex-1 flex flex-col">
              <div>
                <label className="block text-sm font-semibold text-[#073b4c] mb-1">
                  Full Name *
                </label>

                <div className="relative">
                  <FaUser className="absolute left-3 top-1/2 -translate-y-1/2 text-[#1298b5]" />
                  <input
                    type="text"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleChange}
                    required
                    className="w-full pl-10 pr-3 py-3 border border-[#d8f4f8] rounded-xl focus:ring-2 focus:ring-[#1298b5]/30 focus:border-[#1298b5] outline-none"
                    placeholder="John Doe"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-[#073b4c] mb-1">
                  Email *
                </label>

                <div className="relative">
                  <FaEnvelope className="absolute left-3 top-1/2 -translate-y-1/2 text-[#1298b5]" />
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full pl-10 pr-3 py-3 border border-[#d8f4f8] rounded-xl focus:ring-2 focus:ring-[#1298b5]/30 focus:border-[#1298b5] outline-none"
                    placeholder="you@example.com"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-[#073b4c] mb-1">
                  Phone (Optional)
                </label>

                <div className="relative">
                  <FaPhone className="absolute left-3 top-1/2 -translate-y-1/2 text-[#1298b5]" />
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full pl-10 pr-3 py-3 border border-[#d8f4f8] rounded-xl focus:ring-2 focus:ring-[#1298b5]/30 focus:border-[#1298b5] outline-none"
                    placeholder="+91 9876543210"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-[#073b4c] mb-2">
                  Select Amount (₹) *
                </label>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
                  {presetAmounts.map((amt) => (
                    <button
                      key={amt}
                      type="button"
                      onClick={() => handleAmountSelect(amt)}
                      className={`py-2.5 px-4 rounded-xl border font-semibold transition-all ${
                        selectedAmount === amt
                          ? "bg-[#1298b5] text-white border-[#1298b5] shadow-md"
                          : "bg-[#eafaff] text-[#073b4c] border-[#d8f4f8] hover:bg-[#1298b5] hover:text-white"
                      }`}
                    >
                      ₹{amt}
                    </button>
                  ))}
                </div>

                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#1298b5] font-bold">
                    ₹
                  </span>

                  <input
                    type="number"
                    name="customAmount"
                    value={customAmount}
                    onChange={handleCustomAmount}
                    placeholder="Custom amount"
                    className="w-full pl-8 pr-3 py-3 border border-[#d8f4f8] rounded-xl focus:ring-2 focus:ring-[#1298b5]/30 focus:border-[#1298b5] outline-none"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-[#073b4c] mb-2">
                  Payment Method *
                </label>

                <div className="space-y-2">
                  {["card", "upi", "netbanking"].map((method) => (
                    <label
                      key={method}
                      className="flex items-center bg-[#f8fdfe] border border-[#d8f4f8] rounded-xl px-3 py-2 cursor-pointer hover:border-[#1298b5]/50 transition"
                    >
                      <input
                        type="radio"
                        name="paymentMethod"
                        value={method}
                        checked={formData.paymentMethod === method}
                        onChange={handleChange}
                        className="h-4 w-4 accent-[#1298b5]"
                      />

                      <span className="ml-2 text-gray-700 capitalize text-sm">
                        {method === "card"
                          ? "Credit / Debit Card"
                          : method === "upi"
                          ? "UPI (Google Pay/PhonePe/Paytm)"
                          : "Net Banking"}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              <button
                type="submit"
                className="w-full bg-gradient-to-r from-[#1298b5] to-[#0b819b] hover:from-[#f97316] hover:to-[#fb923c] text-white py-4 rounded-xl font-bold text-lg transition-all duration-300 flex items-center justify-center gap-2 shadow-lg mt-auto"
              >
                <FaHeart /> Donate Now
              </button>
            </form>

            <p className="text-xs text-gray-500 mt-4 text-center flex items-center justify-center gap-1">
              <FaShieldAlt className="text-[#1298b5]" />
              Your donation is secure and tax-deductible under Section 80G.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DonationPage;